/**
 * Main-thread wrapper around the AudioWorklet.
 *
 * Everything the UI does to the engine goes through here, so the worklet's
 * message protocol stays in one place.
 */
import workletUrl from './worklet/processor.ts?worker&url';
import type { MixerSettings } from './dsp/mixer';
import type { EngineState } from './engine';
import type { Song } from './types';

export interface EngineSnapshot {
  state: EngineState;
  peaks: Float32Array;
  scopes: Float32Array;
  scopeLength: number;
  masterPeak: [number, number];
}

type SnapshotListener = (snapshot: EngineSnapshot) => void;

export class AudioEngine {
  private context: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private gain: GainNode | null = null;
  private listeners = new Set<SnapshotListener>();
  private readyPromise: Promise<void> | null = null;
  private pendingSong: Song | null = null;

  /** Set once the worklet has registered and is producing audio. */
  ready = false;

  get sampleRate(): number {
    return this.context?.sampleRate ?? 44100;
  }

  get audioContextState(): AudioContextState | 'closed' {
    return this.context?.state ?? 'closed';
  }

  /**
   * Create the context and load the worklet. Safe to call repeatedly — browsers
   * will not start an AudioContext outside a user gesture, so the UI calls this
   * again on the first click.
   */
  async init(): Promise<void> {
    if (this.readyPromise) {
      await this.readyPromise;
      await this.context?.resume();
      return;
    }

    this.readyPromise = (async () => {
      const Ctx: typeof AudioContext =
        window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) throw new Error('This browser has no Web Audio support.');

      const context = new Ctx({ latencyHint: 'interactive' });
      this.context = context;

      if (!context.audioWorklet) {
        throw new Error('This browser has no AudioWorklet support, which FinTracker needs for glitch-free playback.');
      }

      await context.audioWorklet.addModule(workletUrl);

      const node = new AudioWorkletNode(context, 'tracker-processor', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
      node.port.onmessage = (event) => this.onWorkletMessage(event.data);

      const gain = context.createGain();
      gain.gain.value = 1;
      node.connect(gain).connect(context.destination);

      this.node = node;
      this.gain = gain;
      this.ready = true;

      if (this.pendingSong) {
        this.loadSong(this.pendingSong);
        this.pendingSong = null;
      }
    })();

    await this.readyPromise;
    await this.context?.resume();
  }

  private onWorkletMessage(data: { type: string } & Record<string, unknown>): void {
    if (data.type === 'state') {
      const snapshot = data as unknown as EngineSnapshot;
      for (const listener of this.listeners) listener(snapshot);
    }
  }

  onSnapshot(listener: SnapshotListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private post(message: unknown): void {
    this.node?.port.postMessage(message);
  }

  loadSong(song: Song): void {
    if (!this.ready) {
      this.pendingSong = song;
      return;
    }
    // The worklet gets its own copy; sharing would let editor mutations land
    // mid-tick and produce sounds that do not match the pattern on screen.
    this.post({ type: 'load', song: structuredClone(song) });
  }

  play(): void {
    void this.context?.resume();
    this.post({ type: 'play' });
  }

  pause(): void {
    this.post({ type: 'pause' });
  }

  stop(): void {
    this.post({ type: 'stop' });
  }

  seek(order: number, row = 0): void {
    this.post({ type: 'seek', order, row });
  }

  setMixerSettings(settings: Partial<MixerSettings>): void {
    this.post({ type: 'settings', settings });
  }

  setPlayerOptions(options: { loopSong?: boolean; modPanningCommand?: boolean }): void {
    this.post({ type: 'options', ...options });
  }

  noteOn(voice: number, note: number, instrument: number): void {
    void this.context?.resume();
    this.post({ type: 'noteOn', voice, note, instrument });
  }

  noteOff(voice: number): void {
    this.post({ type: 'noteOff', voice });
  }

  allNotesOff(): void {
    this.post({ type: 'allNotesOff' });
  }

  setChannelMuted(channel: number, muted: boolean): void {
    this.post({ type: 'mute', channel, muted });
  }

  clearMutes(): void {
    this.post({ type: 'soloOff' });
  }

  /**
   * Push a single edited pattern. The editor calls this on every keystroke, so
   * it deliberately avoids re-sending instruments — a song with a few megabytes
   * of samples would otherwise be cloned on each cursor move.
   */
  patchPattern(index: number, pattern: { rows: number; data: Uint8Array }): void {
    const copy = pattern.data.slice();
    this.node?.port.postMessage(
      { type: 'patchPattern', index, rows: pattern.rows, data: copy },
      [copy.buffer],
    );
  }

  patchInstrument(index: number, instrument: Song['instruments'][number]): void {
    this.post({ type: 'patchInstrument', index, instrument: structuredClone(instrument) });
  }

  patchMeta(meta: Partial<Song>): void {
    this.post({ type: 'patchMeta', meta: structuredClone(meta) });
  }

  setMasterVolume(value: number): void {
    if (this.gain) this.gain.gain.value = Math.max(0, Math.min(2, value));
  }

  async close(): Promise<void> {
    this.listeners.clear();
    this.node?.disconnect();
    this.gain?.disconnect();
    await this.context?.close();
    this.context = null;
    this.node = null;
    this.ready = false;
    this.readyPromise = null;
  }
}

export const audioEngine = new AudioEngine();
